import { Icon, BrandMark } from '../../components/ui/Icon.js';
import { useState, type FormEvent } from 'react';
import { login, ApiClientError } from '../../lib/api/client.js';

export function LoginPage({ onSignedIn }: { onSignedIn: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      await onSignedIn();
    } catch (err) {
      // The server deliberately does not distinguish "no such account" from
      // "wrong password" (doc 06 §2.1), and neither does this.
      setError(
        err instanceof ApiClientError && err.status === 503
          ? 'The sign-in service is temporarily unavailable. Please try again shortly.'
          : err instanceof Error ? err.message : 'Sign in failed',
      );
      setBusy(false);
    }
  };

  return (
    <div className="arc-login">
      <aside className="arc-login-brand">
        <div className="arc-row" style={{ gap: 10 }}>
          <BrandMark size={30} />
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-.01em' }}>ArcNet</span>
        </div>

        <div>
          <p className="arc-login-lede">
            One platform for every extension, number and minute.
          </p>
          <ul className="arc-login-points" style={{ marginTop: 22 }}>
            <li><Icon name="check" size={14} />Multi-tenant PBX, one SIP domain per customer</li>
            <li><Icon name="check" size={14} />Queues, IVRs, recording and voicemail</li>
            <li><Icon name="check" size={14} />Rated call records and billing in one place</li>
          </ul>
        </div>

        <div className="arc-login-foot">Cloud communications for South Africa</div>
      </aside>

      <div className="arc-login-panel">
      <div className="arc-login-card">
        <div style={{ marginBottom: 20 }}>
          <h1 className="arc-h1">Sign in</h1>
          <p className="arc-muted" style={{ marginTop: 4, fontSize: 13 }}>
            Use your ArcNet account.
          </p>
        </div>

        <form onSubmit={submit} noValidate>
          {error && (
            <div className="arc-banner arc-banner-danger" style={{ marginBottom: 16 }} role="alert">
              <Icon name="warning" size={14} />
              <span>{error}</span>
            </div>
          )}

          <div className="arc-field">
            <label className="arc-label" htmlFor="email">Email address</label>
            <input
              id="email" type="email" className="arc-input"
              autoComplete="username" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              aria-invalid={error !== null}
            />
          </div>

          <div className="arc-field">
            <label className="arc-label" htmlFor="password">Password</label>
            <input
              id="password" type="password" className="arc-input"
              autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)}
              aria-invalid={error !== null}
            />
          </div>

          <button
            type="submit"
            className="arc-btn arc-btn-primary"
            style={{ width: '100%', marginTop: 4 }}
            disabled={busy || !email || !password}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="arc-subtle" style={{ marginTop: 16, fontSize: 12 }}>
          Protected by rate limiting and audit logging.
        </p>
      </div>
      </div>
    </div>
  );
}
